// Issue #67, #177: Shared comment input component with anonymous toggle
// Issue #115: WCAG 2.1 AA accessible with keyboard support
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared CommentInput component with anonymous toggle
 * Used in LandingPage.js and MyPosts.js
 *
 * @param {string} postId - ID of the post being commented on
 * @param {string} value - Current comment text
 * @param {function} onChange - Handler for text changes
 * @param {boolean} isAnonymous - Whether anonymous mode is enabled
 * @param {function} onAnonymousChange - Handler for anonymous toggle
 * @param {function} onSubmit - Handler for submit button
 * @param {boolean} isSubmitting - Whether comment is being submitted
 */
const CommentInput = ({
  postId,
  value,
  onChange,
  isAnonymous,
  onAnonymousChange,
  onSubmit,
  isSubmitting
}) => {
  const { t } = useLanguage();

  const handleTextChange = (e) => {
    onChange(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onAnonymousChange(!isAnonymous);
    }
  };

  return (
    <div className="comment-input-container">
      <textarea
        className="comment-input"
        placeholder={t('comments.writeComment')}
        value={value}
        onChange={handleTextChange}
        style={{ overflow: 'hidden', resize: 'none' }}
        aria-label={t('comments.writeComment')}
      />
      <div className="comment-controls">
        <label className="anonymous-toggle">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => onAnonymousChange(e.target.checked)}
            onKeyDown={handleKeyDown}
            aria-label={t('comments.anonymous') || 'Anonym kommentieren'}
          />
          <span className="toggle-switch" aria-hidden="true"></span>
          <span className="toggle-label">{t('comments.anonymous')}</span>
        </label>
        <button
          className="comment-submit-button"
          onClick={onSubmit}
          disabled={isSubmitting || !value?.trim()}
          aria-label={isSubmitting ? t('comments.sending') : t('send')}
        >
          {isSubmitting ? t('comments.sending') || 'Sende...' : t('send')}
        </button>
      </div>
    </div>
  );
};

export default CommentInput;
